import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { CustomEditor } from '../../core/CustomEditor';
import { DocumentCard } from '../../DocumentPortal/DocumentSortingDialog/DocumentCard';
import type { IDocument } from '../../Interfaces/IDocument';
import {
  initSendDocumentNew,
  ISendDocumentDocumentTypeSchema,
} from '../../Interfaces/ISendDocumentNew';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { useGetSystemMail } from '../../hooks/data';

type DocumentType = (typeof ISendDocumentDocumentTypeSchema.options)[number];

interface ISendDocumentProps {
  legalTransactionFullObject: ILegalTransactionFullObject;
  sendDocument: ReturnType<typeof initSendDocumentNew>;
  setSendDocument: React.Dispatch<
    React.SetStateAction<ReturnType<typeof initSendDocumentNew>>
  >;
}

export const SendDocumentNew: React.FC<ISendDocumentProps> = ({
  legalTransactionFullObject,
  sendDocument,
  setSendDocument,
}) => {
  const [selectedStandaloneTypes, setSelectedStandaloneTypes] = useState<
    DocumentType[]
  >([]);
  const [selectedDocs, setSelectedDocs] = useState<IDocument[]>([]);
  const [selectedLegalClients, setSelectedLegalClients] = useState<any[]>([]);

  const { systemMail, isLoadingSystemMail } = useGetSystemMail(31);

  useEffect(() => {
    setSendDocument(prev => ({
      ...prev,
      legalClientArray: selectedLegalClients,
    }));
  }, [selectedLegalClients, setSendDocument]);

  const certificates = (
    legalTransactionFullObject.CertificateArray || []
  ).filter(doc => doc.FileName.toLowerCase().endsWith('.pdf'));
  const invoices = (legalTransactionFullObject.InvoiceArray || []).filter(doc =>
    doc.FileName.toLowerCase().endsWith('.pdf')
  );
  const drafts = (legalTransactionFullObject.DraftArray || []).filter(doc =>
    doc.FileName.toLowerCase().endsWith('.pdf')
  );
  const docsFromRequirements =
    legalTransactionFullObject.LegalPhaseArray.flatMap(phase =>
      phase.LegalRequirementArray.flatMap(
        req => req.DocumentArrayEmployees || []
      )
    ).filter(doc => doc.FileName.toLowerCase().endsWith('.pdf'));

  const toggleStandaloneType = (type: DocumentType, checked: boolean) => {
    setSelectedStandaloneTypes(prev =>
      checked ? [...prev, type] : prev.filter(t => t !== type)
    );
  };

  useEffect(() => {
    setSendDocument(prev => ({
      ...prev,
      selectedDocuments: selectedDocs,
    }));
  }, [selectedDocs, setSendDocument]);

  const isDocSelected = (doc: IDocument) =>
    selectedDocs.some(d => d.idDocument === doc.idDocument);
  const handleDocSelectChange = (doc: IDocument, selected: boolean) => {
    setSelectedDocs(prev => {
      if (selected) {
        return prev.some(d => d.idDocument === doc.idDocument)
          ? prev
          : [...prev, doc];
      } else {
        return prev.filter(d => d.idDocument !== doc.idDocument);
      }
    });
  };

  const toggleSelectAll = (docs: IDocument[]) => {
    const allSelected =
      docs.length > 0 && docs.every(doc => isDocSelected(doc));
    docs.forEach(doc => {
      handleDocSelectChange(doc, !allSelected);
    });
  };

  const selectedGroupTypes = useMemo(() => {
    const types: DocumentType[] = [];
    if (certificates.some(doc => isDocSelected(doc))) {
      types.push('Record');
    }
    if (invoices.some(doc => isDocSelected(doc))) {
      types.push('Invoice');
    }
    if (drafts.some(doc => isDocSelected(doc))) {
      types.push('Drafts');
    }
    if (docsFromRequirements.some(doc => isDocSelected(doc))) {
      types.push('DocumentsFromRequirements');
    }
    return types;
  }, [certificates, invoices, drafts, docsFromRequirements, selectedDocs]);

  const finalDocumentTypes = useMemo(() => {
    const union = Array.from(
      new Set([...selectedGroupTypes, ...selectedStandaloneTypes])
    );
    return union;
  }, [selectedGroupTypes, selectedStandaloneTypes]);

  const totalSelectedCount = useMemo(() => {
    return selectedDocs.length + selectedStandaloneTypes.length;
  }, [selectedDocs, selectedStandaloneTypes]);

  useEffect(() => {
    if (
      JSON.stringify(sendDocument.documentTypeArray) !==
      JSON.stringify(finalDocumentTypes)
    ) {
      setSendDocument(prev => ({
        ...prev,
        documentTypeArray: finalDocumentTypes,
      }));
    }
  }, [finalDocumentTypes, setSendDocument, sendDocument.documentTypeArray]);

  useEffect(() => {
    if (systemMail && sendDocument.emailText === '') {
      setSendDocument(prev => ({
        ...prev,
        emailText: systemMail.Body,
        subject: systemMail.Subject,
      }));
    }
  }, [systemMail, sendDocument.emailText, setSendDocument]);

  if (isLoadingSystemMail) {
    return (
      <Box
        sx={{
          height: '30vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!systemMail) {
    return (
      <Box
        sx={{
          height: '30vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography>
          Es gab einen Fehler beim Laden der automatischen Email
        </Typography>
      </Box>
    );
  }

  const renderGroup = (groupTitle: string, docs: IDocument[]) => {
    const selectedCount = docs.filter(doc => isDocSelected(doc)).length;
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display='flex' alignItems='center' width='100%'>
            <Typography sx={{ flexGrow: 1 }}>
              {groupTitle}{' '}
              {selectedCount > 0 && <em>({selectedCount} ausgewählt)</em>}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    docs.length > 0 && docs.every(doc => isDocSelected(doc))
                  }
                  onChange={() => toggleSelectAll(docs)}
                  onClick={e => e.stopPropagation()}
                />
              }
              label='alle auswählen'
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {docs.map((doc, index) => (
              <Grid item xs={4} key={`${groupTitle}-${index}`}>
                <DocumentCard
                  initialDocument={doc}
                  selectable={true}
                  selected={isDocSelected(doc)}
                  onSelectChange={selected =>
                    handleDocSelectChange(doc, selected)
                  }
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderNonExpandableStandalone = (
    groupTitle: string,
    type: DocumentType
  ) => (
    <Accordion expanded={false} disableGutters>
      <AccordionSummary expandIcon={null}>
        <Box display='flex' alignItems='center' width='100%'>
          <Typography sx={{ flexGrow: 1 }}>{groupTitle}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                disabled={groupTitle == 'Digitale Nebenakte'}
                checked={selectedStandaloneTypes.includes(type)}
                onChange={e => toggleStandaloneType(type, e.target.checked)}
                onClick={e => e.stopPropagation()}
              />
            }
            label='auswählen'
          />
        </Box>
      </AccordionSummary>
    </Accordion>
  );

  const renderDocumentsAccordion = () => (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography>
          Dokumente{' '}
          {totalSelectedCount > 0 && <em>({totalSelectedCount} ausgewählt)</em>}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {renderGroup('Urkunden', certificates)}
        {renderGroup('Rechnungen', invoices)}
        {renderGroup('Entwürfe', drafts)}
        {renderGroup('Dokumente aus Maßnahmen', docsFromRequirements)}
        {renderNonExpandableStandalone(
          'E-Mails aus Maßnahmen',
          'EmailsFromRequirements'
        )}
        {renderNonExpandableStandalone('Digitale Nebenakte', 'SideFile')}
      </AccordionDetails>
    </Accordion>
  );

  const renderMandantenAccordion = () => {
    const legalClients = legalTransactionFullObject.LegalClientArray || [];
    const clientColumns: GridColDef[] = [
      { field: 'idLegalClient', headerName: 'Nr.', width: 100 },
      {
        field: 'FirstName',
        headerName: 'Vorname',
        flex: 1,
        valueGetter: params => params.row.FirstName || '',
      },
      {
        field: 'LastName',
        headerName: 'Nachname',
        flex: 1,
        valueGetter: params => params.row.LastName || '',
      },
      {
        field: 'Email',
        headerName: 'E-Mail',
        flex: 1,
        valueGetter: params => params.row.Email || '',
      },
    ];
    const selectedClientCount = legalClients.filter(
      client =>
        client.Email &&
        selectedLegalClients.some(
          sc => sc.idLegalClient === client.idLegalClient
        )
    ).length;
    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            Mandanten{' '}
            {selectedClientCount > 0 && (
              <em>({selectedClientCount} ausgewählt)</em>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={legalClients}
              columns={clientColumns}
              getRowId={row => row.idLegalClient}
              checkboxSelection
              disableRowSelectionOnClick
              isRowSelectable={params => !!params.row.Email}
              onRowSelectionModelChange={newSelection => {
                const selected = legalClients.filter(client =>
                  newSelection.includes(client.idLegalClient)
                );
                setSelectedLegalClients(selected);
              }}
              getRowClassName={params =>
                !params.row.Email ? 'notSelectableRow' : ''
              }
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      {renderDocumentsAccordion()}
      {renderMandantenAccordion()}
      {sendDocument.emailText !== '' && (
        <Box mt={2}>
          <CustomEditor
            txt={sendDocument.emailText}
            setTxt={(txt: string) =>
              setSendDocument({ ...sendDocument, emailText: txt })
            }
            dictonaryIdsArray={[1, 2, 3]}
          />
        </Box>
      )}
    </>
  );
};
